import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Construction } from "lucide-react";

const STUBS = {
  "/qcca/boardroom/templates": {
    title: "Template Vault",
    tagline: "Peer-reviewed starters.",
    body: "Meeting minutes templates, civil-enforcement packages, bylaw amendment drafts, dispute-resolution letters — contributed by verified boards and lightly reviewed before they hit the vault.",
    next: "First batch: AGM minutes, audit-waiver resolutions, chargeback notices. Have a template your board uses? Email templates@qcca.ca and we'll credit you.",
  },
  "/qcca/boardroom/forum": {
    title: "Director Forum",
    tagline: "Candid, board-only.",
    body: "A private discussion board for verified directors only. No property managers. No vendors. Just the people who hold the legal responsibility for their buildings, sharing what's actually working.",
    next: "Launches once we have twelve verified boards in the network. Applying today puts you in the first cohort.",
  },
  "/qcca/boardroom/room": {
    title: "Live Board Room",
    tagline: "Meet when it matters.",
    body: "Encrypted video rooms for emergency quorums, cross-corp strategy sessions, and joint presentations to the city. Recordings are made available only to confirmed attendees.",
    next: "Scheduled for Q3 2026 — pending the outcome of an early-network survey on what formats (drop-in, scheduled, open mic) members want.",
  },
  "/qcca/boardroom/ratings": {
    title: "Property Manager Ratings",
    tagline: "The reputation ledger.",
    body: "A crowdsourced, verified-director-only ledger of property management firms and service providers operating in Regina. Rate, review, and surface patterns — if a firm isn't serving condo owners with integrity, the network finds out.",
    next: "Includes an anonymous mode for directors whose management contracts include non-disparagement clauses. We're working with legal counsel on the exact mechanism before launch.",
  },
  "/qcca/boardroom/alerts": {
    title: "City Policy Alerts",
    tagline: "Never be the last to know.",
    body: "Automated scraping + human curation of Regina City Council agendas, committee papers, and bylaw notices that touch condo properties — taxation, zoning, waste collection, building code, heritage designations.",
    next: "V1 sends an email the morning an item lands on an agenda. V2 includes a one-click delegation-request template the board can submit back to the city.",
  },
};

export default function QCCABoardroomStub() {
  const { pathname } = useLocation();
  const content = STUBS[pathname] || {
    title: "Coming soon",
    tagline: "This tool is on the roadmap.",
    body: "This feature is part of the QCCA toolkit and is being built out in the coming months.",
    next: "",
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Link to="/qcca/boardroom" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-10">
          <ArrowLeft className="w-3 h-3" /> Boardroom
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 flex items-center justify-center border border-[#C5A52A]/50 text-[#C5A52A]">
            <Construction className="w-5 h-5" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Coming soon</div>
        </div>

        <h1 className="font-headline text-4xl lg:text-5xl text-white leading-tight" data-testid="qcca-stub-title">{content.title}</h1>
        <div className="mt-2 font-headline italic text-2xl text-[#C5A52A]">{content.tagline}</div>
        <p className="mt-8 text-gray-300 text-lg leading-relaxed">{content.body}</p>
        {content.next && (
          <div className="mt-10 border-l-2 border-[#C5A52A] pl-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A]">Planned</div>
            <p className="mt-2 text-gray-400 leading-relaxed">{content.next}</p>
          </div>
        )}

        <div className="mt-14 bg-[#0a0a0a] border border-[#1a1a1a] p-6 flex items-start gap-4">
          <div className="text-[#C5A52A]">💡</div>
          <div className="text-sm text-gray-400 leading-relaxed">
            <span className="text-white">Meanwhile —</span> Secure Vote-by-Email is live now and ready for your next AGM-waiver or audit-waiver resolution.{" "}
            <Link to="/qcca/boardroom/votes" className="text-[#C5A52A] hover:text-white">Run a vote →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
